'use client';

import { useEffect } from 'react';

import {
  useDeviceAssessmentValidationQuery,
  useTenantConfigQuery,
} from '@northladder/apis';
import { AppHeader, PublicFooter } from '@northladder/design-system';
import {
  useDeviceAssessmentStore,
  useRegionStore,
} from '@northladder/services';

import {
  AssessmentReadyToBegin,
  NoValidAssessmentSession,
  useNavRouter,
} from '~/features';
import { PageLayout } from '~/layouts';

/**
 * -----------------------------------------------------------------------------
 * This displays the entry point for the device assessment, showing the
 * placeholder logos and a button to start the assessment.
 *
 * TODO: Add logic to navigate the user back to this page within a common page
 * route if they have not completed the previous assessment or when the `eii`
 * is invalid.
 */
export default function Home() {
  const STEP_INDEX = 0;
  const NEXT_PAGE = '/assessment-instructions';

  const {
    evaluationId,
    navigateToRoute,
    prefetchRoute,
    resetNavPath,
    platform,
    sessionType,
  } = useNavRouter();

  const { onResetDeviceAssessmentStore } = useDeviceAssessmentStore();
  const { onUpdateTenantConfig } = useRegionStore();

  const {
    data: tenantConfigData,
    refetch: refetchConfig,
    isPending: isLoadingConfigBaseURL,
    error: configError,
  } = useTenantConfigQuery({
    identifier: platform,
  });

  useEffect(() => {
    if (tenantConfigData) {
      onUpdateTenantConfig(tenantConfigData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantConfigData]);

  const handleOnResetAssessment = () => {
    onResetDeviceAssessmentStore(evaluationId);
  };

  const {
    isPending: isValidatingSession,
    error: validationError,
    data,
    refetch,
  } = useDeviceAssessmentValidationQuery({
    evaluationId,
    isEnabled: !!tenantConfigData?.apiBaseURL,
  });

  useEffect(() => {
    if (data?.productName) {
      handleOnResetAssessment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (validationError) {
      handleOnResetAssessment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationError]);

  useEffect(() => {
    onResetDeviceAssessmentStore(evaluationId);
    prefetchRoute(NEXT_PAGE);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluationId]);

  const handleRestartSession = () => {
    resetNavPath();
  };

  /**
   * Handles the click event of the "Start" button.
   * Navigates the user to the assessment instructions page.
   */
  const handleOnStart = () => {
    navigateToRoute(NEXT_PAGE);
  };

  /**
   * If the app failed to loads the configuration or the validation, retry, but
   * consider the config first as the validation depends on it.
   */
  const handleRetryValidation = () => {
    if (configError) {
      refetchConfig();
    } else if (validationError) {
      refetch();
    }
  };

  const hasNoValidSession =
    !evaluationId ||
    isValidatingSession ||
    isLoadingConfigBaseURL ||
    validationError ||
    !data?.isValid;

  const isAssessmentDone =
    data?.productName === null ? data?.isAssessmentDone : false;

  return (
    <PageLayout className="app-bg-grad" stepIndex={STEP_INDEX}>
      <div className="flex min-h-screen flex-col overflow-hidden">
        <AppHeader />

        {hasNoValidSession ? (
          <NoValidAssessmentSession
            error={validationError}
            hasInvalidEvaluationId={!data?.isValid}
            hasInvalidSession={sessionType === 'invalid'}
            hasNoEvaluationId={!evaluationId}
            isAssessmentDone={isAssessmentDone}
            isLoading={isValidatingSession || isLoadingConfigBaseURL}
            onRestart={handleRestartSession}
            onRetry={handleRetryValidation}
          />
        ) : (
          <AssessmentReadyToBegin onStart={handleOnStart} />
        )}
      </div>
      <PublicFooter showFooterNav={false} />
    </PageLayout>
  );
}
